import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  type ClipboardScorePenaltyMultipliers,
  type ClipboardScorePolicy,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { snakeCase } from "lodash";
import { useState } from "react";

import { areMultipliersActive } from "../../utils/utils";
import { ActiveMultiplierInfo } from "../ActiveMultiplierInfo";
import {
  AdjustmentsSection,
  type AdjustmentsSectionItem,
  type AdjustmentsSectionProps,
} from "./Section";
import { ShortLeadTimeShiftInfo } from "./ShortLeadTimeShiftInfo";

interface ClipboardScoreAdjustmentsContentProps {
  policy: ClipboardScorePolicy;
  multipliers?: ClipboardScorePenaltyMultipliers;
  useExpectedArrivalTime: boolean;
}

function getWorkingItems(policy: ClipboardScorePolicy): AdjustmentsSectionItem[] {
  const workShiftPoints = policy?.workShift?.points ?? 0;
  const workShiftItem: AdjustmentsSectionItem = {
    title: "Complete a shift",
    consequence: workShiftPoints,
    consequenceType: "bonus",
  };

  return [
    ...(workShiftPoints > 0 ? [workShiftItem] : []),
    {
      title: "Abandon a shift",
      consequence: "Instant restriction",
      consequenceType: "penalty",
    },
  ];
}

function getTimelinessItems(
  policy: ClipboardScorePolicy,
  multiplier?: number
): AdjustmentsSectionItem[] {
  const timelinessPoints = policy?.shiftClockInConfig?.points ?? 0;
  const timelinessItem = {
    title: "Arrive on time",
    consequence: timelinessPoints,
    consequenceType: "bonus" as const,
  };

  const latenessConfig = policy?.latenessConfig ?? [];

  const latenessItems = latenessConfig.map((config) => {
    const { points: consequence = 0, minutesLateRange } = config;
    const { startFrom, stopAt } = minutesLateRange;

    const range = isDefined(stopAt) ? `${startFrom}-${stopAt - 1}` : `${startFrom}+`;
    const title = `${range} minutes`;

    return {
      title,
      consequence,
      consequenceType: "penalty" as const,
      multiplier: multiplier === 1 ? undefined : multiplier,
    };
  });

  return [...(timelinessPoints > 0 ? [timelinessItem] : []), ...latenessItems].sort(
    (a, b) => b.consequence - a.consequence
  );
}

function getCancellationItems(
  policy: ClipboardScorePolicy,
  multiplier?: number
): AdjustmentsSectionItem[] {
  const cancellationConfig = policy?.cancelShift ?? [];

  return cancellationConfig
    .map((config) => {
      const { points: consequence = 0, leadTime } = config;
      const [from, to] = leadTime;

      let title = `${from ?? "0"}`;
      title += isDefined(to) ? `-${to}` : "+";
      title += " hours notice";
      return {
        title,
        consequence,
        consequenceType: "penalty" as const,
        multiplier: multiplier === 1 ? undefined : multiplier,
      };
    })
    .sort((a, b) => b.consequence - a.consequence);
}

function getNoShowItems(): AdjustmentsSectionItem[] {
  return [
    {
      title: "No show",
      consequence: "Instant restriction",
      consequenceType: "penalty",
    },
    {
      title: "Cancel after shift starts",
      consequence: "Instant restriction",
      consequenceType: "penalty",
    },
  ];
}

function getSections(
  policy: ClipboardScorePolicy,
  multipliers?: ClipboardScorePenaltyMultipliers,
  useExpectedArrivalTime = false
) {
  const workingItems = getWorkingItems(policy);
  const timelinessItems = getTimelinessItems(policy, multipliers?.lateness.next);
  const cancellationItems = getCancellationItems(policy, multipliers?.cancellation.next);
  const noShowItems = getNoShowItems();

  const sections: Array<
    Pick<AdjustmentsSectionProps, "title" | "description" | "items" | "callout">
  > = [];

  if (workingItems.length > 0) {
    sections.push({
      title: "Work the full shift",
      items: workingItems,
    });
  }

  if (timelinessItems.length > 0) {
    sections.push({
      title: "Show up on time",
      items: timelinessItems,
      callout: useExpectedArrivalTime ? <ShortLeadTimeShiftInfo /> : undefined,
    });
  }

  if (noShowItems.length > 0) {
    sections.push({
      title: "Never no show",
      items: noShowItems,
    });
  }

  if (cancellationItems.length > 0) {
    sections.push({
      title: "Cancel early",
      description: "The earlier you cancel, the fewer points you lose",
      items: cancellationItems,
    });
  }

  return sections;
}

export function ClipboardScoreAdjustmentsContent({
  policy,
  multipliers,
  useExpectedArrivalTime,
}: ClipboardScoreAdjustmentsContentProps) {
  const sections = getSections(policy, multipliers, useExpectedArrivalTime);

  const [expanded, setExpanded] = useState<string | boolean>(false);

  return (
    <Stack direction="column" spacing={6} paddingTop={5} padding={3}>
      {isDefined(multipliers) && areMultipliersActive(multipliers) && (
        <Stack spacing={7} padding={7} justifyContent="center" alignItems="center">
          <ActiveMultiplierInfo multipliers={multipliers} />
        </Stack>
      )}
      {sections.map((section) => {
        const { title } = section;
        const sectionId = snakeCase(title);

        return (
          <AdjustmentsSection
            key={`adjustment-section-${sectionId}`}
            expanded={expanded === sectionId}
            onClick={() => {
              setExpanded(expanded === sectionId ? false : sectionId);
            }}
            {...section}
          />
        );
      })}
    </Stack>
  );
}
